/** Shared dashboard status presentation and calendar/overview colors. */
import { isCancelledEvent } from "../../../utils/methods/eventPresentation";
import type { EventPresentationState } from "../../../utils/methods/eventPresentation";

export const CALENDAR_STATUS_COLORS: Record<
  string,
  { bg: string; fg: string }
> = {
  Ongoing: { bg: "#00EEC5", fg: "#000" },
  Upcoming: { bg: "#D175B6", fg: "#fff" },
  Past: { bg: "#3a3f5c", fg: "#fff" },
  Cancelled: { bg: "#8b1a1a", fg: "#fff" },
};

const DEFAULT_COLOR = { bg: "#555", fg: "#fff" };

export const calendarStatusColor = (status?: string) =>
  CALENDAR_STATUS_COLORS[status ?? ""] ?? DEFAULT_COLOR;

export type BarLook = { bg: string; fg: string; border?: string; strike?: boolean };

/**
 * Stage colors agreed with the team (Casper, 29/09/2026): past = gray,
 * draft or not approved = white, approved keeps the status colors above.
 */
export const STAGE_LOOKS: Record<"notApproved" | "draft", BarLook> = {
  notApproved: { bg: "#F4F4F6", fg: "#0E121E" },
  draft: { bg: "transparent", fg: "#fff", border: "1px dashed rgba(255,255,255,0.85)" },
};

/** Marked as duplicate (2026-10-05): gray and struck through, only seen in the Duplicates quick view. */
export const DUPLICATE_LOOK: BarLook = { bg: "#5b5f73", fg: "#fff", strike: true };

/**
 * Color of one event bar: cancelled and past first, then the stage
 * (`lifecycle` from getAllEvents / the board), then the time status.
 */
export function eventBarLook(ev: EventPresentationState): BarLook {
  if (ev?.isDuplicate === true || ev?.eventStatus === "Duplicate") return DUPLICATE_LOOK;
  if (isCancelledEvent(ev)) {
    return { ...CALENDAR_STATUS_COLORS.Cancelled, strike: true };
  }
  if (ev?.eventStatus === "Past") return CALENDAR_STATUS_COLORS.Past;
  if (ev?.lifecycle === "draft") return STAGE_LOOKS.draft;
  if (ev?.lifecycle === "newRequest" || ev?.lifecycle === "pendingApproval") {
    return STAGE_LOOKS.notApproved;
  }
  return calendarStatusColor(ev?.eventStatus);
}

/** Legend shared by the calendar and the overview. */
export const EVENT_BAR_LEGEND: { label: string; look: BarLook }[] = [
  { label: "Ongoing", look: CALENDAR_STATUS_COLORS.Ongoing },
  { label: "Upcoming", look: CALENDAR_STATUS_COLORS.Upcoming },
  { label: "Not approved", look: STAGE_LOOKS.notApproved },
  { label: "Draft", look: STAGE_LOOKS.draft },
  { label: "Past", look: CALENDAR_STATUS_COLORS.Past },
  { label: "Cancelled", look: { ...CALENDAR_STATUS_COLORS.Cancelled, strike: true } },
];
